import React from "react";
import { AlertTriangle } from "lucide-react";
import { EmptyState } from "../../molecules/EmptyState";
import { routeErrorBoundaryCopy as copy } from "./RouteErrorBoundary.copy";
import { isChunkLoadError, reloadOnceForChunk } from "./routeErrorBoundary.helpers";

export interface RouteErrorBoundaryProps {
  children: React.ReactNode;
  /** A change (e.g. the route's path and query) clears the error, so navigating away recovers. */
  resetKey?: string;
  /** Reloads the page; defaults to window.location.reload (injectable for tests). */
  onReload?: () => void;
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * Catches a render error below a route and shows a recoverable error state
 * instead of unmounting the app (a blank page). A stale lazy chunk after a
 * deploy reloads the page once on its own.
 */
export class RouteErrorBoundary extends React.Component<RouteErrorBoundaryProps, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("RouteErrorBoundary caught an error", error, info.componentStack);
    this.props.onError?.(error, info);
    if (isChunkLoadError(error)) reloadOnceForChunk(window.location.href, this.reload);
  }

  componentDidUpdate(prev: RouteErrorBoundaryProps): void {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  private reload = (): void => {
    if (this.props.onReload) this.props.onReload();
    else window.location.reload();
  };

  private retry = (): void => this.setState({ error: null });

  render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const isChunk = isChunkLoadError(error);
    return (
      <EmptyState
        tone="error"
        announce
        icon={<AlertTriangle aria-hidden />}
        title={copy.title}
        caption={isChunk ? copy.chunkCaption : copy.caption}
        action={isChunk ? { label: copy.reload, onClick: this.reload } : { label: copy.retry, onClick: this.retry }}
        secondaryAction={isChunk ? null : { label: copy.reload, onClick: this.reload }}
      />
    );
  }
}

export default RouteErrorBoundary;
